/**
 * Storm Hooks
 *
 * React Query hooks for Hurricane Impact Prediction (HIP) storm endpoints.
 * All hooks use proper cache keys and staleTime configuration for optimal performance.
 */

import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { stormsApi } from "@/api/services";
import type { ScenarioQueryParams } from "@/api/types";

/**
 * Fetch list of storms.
 *
 * @param activeOnly    - Filter to only active storms (default: true)
 * @param stormApplicationId - Optional storm_application filter (e.g. WIP app id).
 *   `undefined` → no filter (HIP default).
 *   `null`      → filter pending (query disabled until resolved).
 *   `string`    → apply filter.
 */
export function useStorms(
  activeOnly: boolean = true,
  stormApplicationId?: string | null,
) {
  const shouldFilter = stormApplicationId !== undefined;
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [
      "storms",
      "list",
      { activeOnly, stormApplicationId: stormApplicationId ?? null },
    ],
    queryFn: () =>
      stormApplicationId == null
        ? stormsApi.list(activeOnly)
        : stormsApi.list(activeOnly, stormApplicationId),
    staleTime: 5 * 60 * 1000,
    enabled: shouldFilter ? !!stormApplicationId : true,
  });

  return { data, loading: isLoading, isFetching, error };
}

/**
 * Fetch metadata for a specific storm
 * Returns available weather models and slider ranges
 *
 * @param stormId - UUID of the storm
 */
export function useStormMetadata(stormId: string | null) {
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["storms", "metadata", stormId],
    queryFn: () => stormsApi.getMetadata(stormId!),
    enabled: !!stormId,
    staleTime: Infinity, // Metadata doesn't change for a given storm
  });

  return { data, loading: isLoading, isFetching, error };
}

/**
 * Fetch scenario snapshot based on deviation parameters
 * Results are cached based on all parameters - same params = same snapshot
 *
 * @param stormId - UUID of the storm
 * @param params - Scenario parameters (model, intensity, track, speed)
 */
export function useStormScenario(
  stormId: string | null,
  params: ScenarioQueryParams | null
) {
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [
      "storms",
      "scenario",
      stormId,
      params
        ? {
            weather_model_name: params.weather_model_name,
            intensity: params.intensity,
            track: params.track,
            speed: params.speed,
          }
        : null,
    ],
    queryFn: () => stormsApi.getScenario(stormId!, params!),
    enabled: !!stormId && !!params,
    staleTime: Infinity, // Scenario is deterministic based on params
    placeholderData: keepPreviousData, // Keep previous data while loading new scenario
  });

  return { data, loading: isLoading, isFetching, error };
}

/**
 * Fetch track GeoJSON for a specific snapshot
 *
 * @param snapshotId - UUID of the track snapshot
 */
export function useTrackGeoJSON(snapshotId: string | null) {
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["storms", "snapshots", snapshotId, "track"],
    queryFn: () => stormsApi.getTrack(snapshotId!),
    enabled: !!snapshotId,
    staleTime: Infinity, // Track never changes for a given snapshot
    placeholderData: keepPreviousData, // Keep previous track while loading new one
  });

  return { data, loading: isLoading, isFetching, error };
}

/**
 * Fetch impact forecasts for a specific snapshot
 *
 * @param snapshotId - UUID of the track snapshot
 */
export function useStormImpacts(snapshotId: string | null) {
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["storms", "snapshots", snapshotId, "impacts"],
    queryFn: () => stormsApi.getImpacts(snapshotId!),
    enabled: !!snapshotId,
    staleTime: Infinity, // Impacts never change for a given snapshot
    placeholderData: keepPreviousData, // Keep previous impacts while loading new ones
  });

  return { data, loading: isLoading, isFetching, error };
}
